import { ListingType } from "../../types/userListing";

// Platforms a promotion campaign can never post to. Single source of truth:
// the manual picker (ChoosePlatforms) and the Autopilot preselect
// (CreateCampaign) both read this list, so allowing a platform for promotions
// later is a one-line removal here and every flow picks it up together.
export const PROMOTION_DISALLOWED_PLATFORMS: readonly string[] = [
    "google",
    "gmb",
    "pinterest",
    "youtube",
];

// A manually scheduled promotion posts at most twice a week on each channel
// it can post to. Mirrors the api rule (promotion-platform-rules.ts), which
// refuses an over-cap schedule with a 400; the wizard's gates keep the user
// from reaching that. Keyed by the platform names the wizard schedules with.
export const MANUAL_PROMOTION_WEEKLY_POST_CAP: Readonly<Record<string, number>> =
    {
        Facebook: 2,
        Instagram: 2,
        LinkedIn: 2,
        Twitter: 2,
    };

export interface ChoosePlatformsProps {
    setCurrentStep?: (step: number) => void;
    // MB-1421: overrides the default advance-to-step-2. The Autopilot promo
    // wizard is one step - Next creates the campaign directly.
    onNext?: () => void;
    selectedPlatforms: SocialsConnected;
    setSelectedPlatforms: (platforms: SocialsConnected) => void;
    from?: string | null;
    listingId?: string | null;
    listingType?: string | null;
    instagramDisabled?: boolean;
    isVideo?: boolean;
    selectedListing?: {
        id?: string;
        address?: string;
        price?: number;
        image?: string;
        listingType: string;
    } | null;
    youtubeDisabled?: boolean;
    hasMedia?: boolean;
    enableMediaCapability?: boolean;
    isAdHoc?: boolean;
    // "All video" / "All images" quick-select presets also stamp the wizard's
    // explicit media-type choice so the Select Media step opens on that side
    // even after later platform edits (MB-1492).
    onMediaTypePreset?: (type: "image" | "video") => void;
    // Remaining posts on the user's plan this month - a quick-select preset
    // refuses (with a toast) to select more platforms than the plan can post.
    remainingPostLimit?: number;
    onYoutubeProfileUrl?: (url: string) => void;
    onConnectionData?: (profiles: Record<string, string>) => void;
}

export interface LocationState {
    listingId: string;
    listingType: string;
    from: string;
    parsedFooter: string | undefined;
    parsedGoogleFooter?: string | undefined;
    isVideo?: boolean;
    usableImageCount?: number;
}

export interface SocialsConnected {
    facebook: boolean;
    instagram: boolean;
    twitter: boolean;
    linkedin: boolean;
    pinterest: boolean;
    google: boolean;
    youtube?: boolean;
}

export interface ConnectedSocialsData {
    displayName: string;
    id: string;
    pageName: string;
    platform: string;
    profileUrl: string;
    userImage: string;
    username?: string;
}

export interface ChooseFrequencyProps {
    setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
    selectedPlatforms: SocialsConnected;
    campaignPlatforms: CampaignPlatform[];
    setCampaignPlatforms: React.Dispatch<
        React.SetStateAction<CampaignPlatform[]>
    >;
    listingType: string | null;
    createCampaign: () => void;
    occupiedSlotsMapping: { [platform in SocialPlatforms]?: Set<string> };
    /** MB-1455: weekly Instagram posts used by the OTHER campaigns. */
    instagramPostsUsedByOthers: number;
    getAvailableTimeSlot: (
        occupiedSlots: Set<string>,
        defaultTime: { hour: number; minute: number },
        days: string[]
    ) => { hour: number; minute: number };
    usableImageCount?: number;
    isEditMode?: boolean;
    /** The listing is marketed for another agent; the footer card asks before the footer goes off. */
    sharedListing?: boolean;
}

export const weekDays = [
    { id: "Monday", label: "Monday" },
    { id: "Tuesday", label: "Tuesday" },
    { id: "Wednesday", label: "Wednesday" },
    { id: "Thursday", label: "Thursday" },
    { id: "Friday", label: "Friday" },
    { id: "Saturday", label: "Saturday" },
    { id: "Sunday", label: "Sunday" },
];

export interface ChooseCampaignOptionsProps {
    setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
    autoApprove: boolean;
    setAutoApprove: React.Dispatch<React.SetStateAction<boolean>>;
    autoGenerateSlideshow: boolean;
    setAutoGenerateSlideshow: React.Dispatch<React.SetStateAction<boolean>>;
    listingType: string | null;
    date?: Date;
    setDate?: (date: Date) => void;
    firstImage: boolean;
    setFirstImage: React.Dispatch<React.SetStateAction<boolean>>;
    floorPlan: boolean;
    setFloorPlan: React.Dispatch<React.SetStateAction<boolean>>;
    cta: "bottom" | "top";
    setCta: React.Dispatch<React.SetStateAction<"bottom" | "top">>;
    openHouseMonitoring: boolean;
    setOpenHouseMonitoring: React.Dispatch<React.SetStateAction<boolean>>;
    // MB-1314: auto-video is only usable once the logo + headshot are set up.
    // When false, toggling opens an inline setup dialog instead of blocking.
    autoVideoReady: boolean;
    // Re-fetch auto-video readiness after the inline setup dialog saves.
    onAutoVideoConfigured?: () => void;
    // MB-1405: when set, Continue calls this instead of advancing to the
    // schedule step — Autopilot owns promotion scheduling.
    onNext?: () => void;
}

export interface PostTimeProps {
    setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
    campaignPlatforms: CampaignPlatform[];
    setCampaignPlatforms: React.Dispatch<
        React.SetStateAction<CampaignPlatform[]>
    >;
    parsedFooter: string | undefined;
}

export interface postTime {
    hour: number;
    minute: number;
}

export interface CampaignProgress {
    currentStep: string;
    totalPosts?: number;
    postsCreated?: number;
}

export const allPlatforms = ["Facebook", "Instagram"];

export enum ContentLength {
    Short = "Short",
    Medium = "Medium",
    Long = "Long",
}

export enum SocialPlatforms {
    Facebook = "Facebook",
    Instagram = "Instagram",
    Twitter = "Twitter",
    LinkedIn = "LinkedIn",
    Pinterest = "Pinterest",
    Google = "Google",
    YouTube = "YouTube",
}

export interface CampaignPlatform {
    name: SocialPlatforms;
    postsPerDay: number;
    postTimes: { hour: number; minute: number }[];
    contentLengths: ContentLength[];
    footer: string;
    includeFooter: boolean;
    footerType: "comment" | "in-post";
    threads: boolean;
    frequency: "Daily" | "Weekly";
    dayOfWeek?: string[];
    emojis?: boolean;
    usePrice?: boolean;
    soldPost?: boolean;
    useImage?: boolean;
    youtubeVisibility?: "private" | "public" | "unlisted" | "draft";
  youtubeNotifySubscribers?: boolean;
}

export interface PostFooterInfoProps {
    openInfoDialog: boolean;
    setOpenInfoDialog: React.Dispatch<React.SetStateAction<boolean>>;
    parsedFooter: string | undefined;
    platformName: SocialPlatforms;
}

export interface PostThreadInfoProps {
    openInfoDialog: boolean;
    setOpenInfoDialog: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface ListingInformationConfigurationProps {
    platforms: CampaignPlatform[];
    setCurrentStep: (step: number) => void;
    setPlatforms: React.Dispatch<React.SetStateAction<CampaignPlatform[]>>;
}

export interface PlatformFooterCardProps {
    platform: CampaignPlatform;
    setPlatforms: React.Dispatch<React.SetStateAction<CampaignPlatform[]>>;
    /** The listing is marketed for another agent; ask before the footer goes off. */
    sharedListing?: boolean;
    /** The user already confirmed a footer-off in this wizard visit; skip the prompt. */
    footerOffAcknowledged?: boolean;
    /** Called when the user confirms turning the footer off. */
    onFooterOffAcknowledged?: () => void;
}

export interface CampaignSettingsProps {
    campaignId: string;
    platforms: CampaignPlatform[];
    campaignType: ListingType;
    startDate: Date;
    endDate: Date;
}
